import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { API } from "@/constants/brand";
import { LogoMark } from "@/components/Logo";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const token = params.get("token") || params.get("session_id");
  const [state, setState] = useState("loading");
  const [vouchers, setVouchers] = useState([]);
  const { clear } = useCart();

  useEffect(() => {
    if (!token) {
      setState("error");
      return;
    }
    let tries = 0;
    const run = async () => {
      try {
        const { data } =
          tries === 0
            ? await axios.get(`${API}/payments/capture/${token}`)
            : await axios.get(`${API}/payments/status/${token}`);
        if (data.payment_status === "paid") {
          setState("paid");
          if (data.vouchers?.length) setVouchers(data.vouchers);
          clear();
          return;
        }
      } catch {
        /* keep polling */
      }
      tries += 1;
      if (tries < 10) setTimeout(run, 2500);
      else setState("timeout");
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center px-5 noise-overlay grid-lines">
      <div className="glass-panel rounded-3xl p-10 max-w-md w-full text-center glow-border">
        <div className="flex justify-center mb-6">
          <LogoMark size={56} />
        </div>
        {state === "loading" && (
          <>
            <Loader2 size={40} className="mx-auto text-cyan-400 animate-spin mb-5" />
            <h1 className="text-2xl font-bold text-slate-50">Zahlung wird bestätigt…</h1>
            <p className="text-sm text-slate-400 mt-3">Einen Moment bitte, wir prüfen deine PayPal-Zahlung.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={44} className="mx-auto text-emerald-400 mb-5" />
            <h1 className="text-2xl font-bold text-slate-50">Zahlung erfolgreich!</h1>
            <p className="text-sm text-slate-400 mt-3">
              Vielen Dank für deine Bestellung. M-Line Solutions meldet sich in Kürze bei dir.
            </p>
            {vouchers.length > 0 && (
              <div data-testid="voucher-codes-list" className="mt-6 rounded-2xl border border-cyan-400/25 bg-cyan-400/5 p-5 text-left">
                <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-300 font-mono-tech mb-3">
                  Deine Geschenkkarten-Codes
                </div>
                {vouchers.map((v) => (
                  <div key={v.code} className="flex items-center justify-between gap-3 py-1.5">
                    <span className="text-xs text-slate-400">{v.product_name.split("·")[1]?.trim() || v.product_name}</span>
                    <span className="font-mono-tech text-sm font-bold text-cyan-300">{v.code}</span>
                  </div>
                ))}
                <p className="text-[11px] text-slate-500 mt-3">
                  Die Codes wurden dir zusätzlich per E-Mail geschickt. Einlösbar bei jedem Werkstatt-Termin.
                </p>
              </div>
            )}
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <XCircle size={44} className="mx-auto text-amber-400 mb-5" />
            <h1 className="text-2xl font-bold text-slate-50">Bestätigung ausstehend</h1>
            <p className="text-sm text-slate-400 mt-3">
              Die Zahlungsbestätigung dauert etwas länger. Bei Fragen erreichst du uns jederzeit telefonisch.
            </p>
          </>
        )}
        <Link
          to="/"
          data-testid="payment-success-home-link"
          className="mt-8 inline-flex items-center justify-center rounded-full bg-gradient-to-r from-[#1E6FD9] to-[#29B6F6] px-7 py-3 text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.03]"
        >
          Zurück zur Startseite
        </Link>
      </div>
    </div>
  );
}
